// Batched analytics (TECH_SPEC §4.4). Events queue up and flush on a timer,
// on visibilitychange and on pagehide (sendBeacon so the last batch survives).

import { ensureDeviceId } from './api';
import type { EntryType } from './types';

export type EventName =
  | 'link_open'
  | 'game_start'
  | 'game_end'
  | 'result_view'
  | 'share_click'
  // Rewarded-ad revive (design §8.1/§8.2; 广告失败率单独埋点 = `ad_fail`).
  | 'ad_ready'
  | 'ad_fail'
  | 'ad_revive_shown'
  | 'ad_revive_viewed'
  | 'ad_revive_dismissed'
  // Rival ladder (§4bis.21). `ladder_shown` carries which rungs were offered,
  // `ladder_click` which one was taken — no new plumbing, no K/experiment props.
  | 'ladder_shown'
  | 'ladder_click'
  // 快斗 Blitz Duel (§4bis.28). `duel_start` fires when the countdown ends —
  // the moment a duel is actually offered AND accepted; `duel_result` carries
  // the verdict and the retry count. The four funnel events additionally carry
  // `duelArm` and `duel`, which is why the block above needed no new plumbing.
  | 'duel_start'
  // §4bis.30: `duel_seal` is the MID-RUN beat's own coverage — the frame the
  // line was passed, while the run carries on. It must never fire `result_view`
  // (contractually once per `game_end`) and it is not a `duel_result`: the run
  // has not ended, and half the runs that fire it will die a second later.
  | 'duel_seal'
  | 'duel_result'
  // 帮我 The hand-back (§4bis.31). `handoff_offer` fires WITH the death screen
  // on every eligible death in the arm — offered or not, so T2's denominator is
  // the deaths where the verb COULD have been chosen. It is deliberately not a
  // `result_view`, which is contractually once per `game_end`.
  | 'handoff_offer'
  // Somebody picked the life up. `addressed` is T1, the whole bet: 帮我 has to
  // summon a SPECIFIC person where 打败我 shouts into a chat. If the addressed
  // device is no likelier to open than a stranger, the thesis is falsified.
  | 'handoff_take';

export type EventProps = Record<string, string | number | boolean | null>;

interface QueuedEvent {
  name: EventName;
  runId?: string;
  props: EventProps;
}

const ENDPOINT = '/api/events';
const FLUSH_DELAY_MS = 2000;
const MAX_BATCH = 40;

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

export interface GameEndProps extends EventProps {
  distanceM: number;
  deathFrame: number;
  finished: boolean;
  revives: number;
  entryType: EntryType;
  /** Peak-moment arm as evaluated on-device (§4bis.22); also rides
   *  `game_start`, `result_view` and `share_click`. */
  peakArm: 0 | 1;
  /** Blitz-duel arm as evaluated on-device (§4bis.28); also rides
   *  `game_start`, `result_view` and `share_click`. */
  duelArm: 0 | 1;
  /** Was THIS run a duel? Distinct from the arm: an armed device still plays
   *  full runs, and T4's guardrail is exactly that ratio. */
  duel: 0 | 1;
}

export function track(name: EventName, props: EventProps = {}, runId?: string): void {
  queue.push(runId === undefined ? { name, props } : { name, runId, props });
  if (queue.length >= MAX_BATCH) {
    void flush();
    return;
  }
  if (timer === null) {
    timer = setTimeout(() => {
      timer = null;
      void flush();
    }, FLUSH_DELAY_MS);
  }
}

export function flush(useBeacon = false): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  const payload = JSON.stringify(batch);

  if (useBeacon && typeof navigator.sendBeacon === 'function') {
    // sendBeacon can't set headers; device id rides along in the mirrored cookie.
    const blob = new Blob([payload], { type: 'application/json' });
    if (navigator.sendBeacon(ENDPOINT, blob)) return;
  }

  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json', 'x-device-id': ensureDeviceId() },
    body: payload,
    keepalive: true,
    credentials: 'same-origin',
  }).catch(() => {
    /* analytics must never break the game */
  });
}

export function installAnalyticsFlush(): void {
  if (installed) return;
  installed = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  window.addEventListener('pagehide', () => flush(true));
}
